import { useSelector } from 'react-redux';

import useCheckoutKit from 'hooks/useCheckoutKit';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import useFeature from 'services/features/useFeature';
import useSubscriptions from 'services/general/subscriptionsList';
import { urls } from '@repo/app-constants';
import { IconName } from '@repo/ui';
import { Kit } from '@repo/types';
import { getKitById } from 'store/kits';
import { getKits } from 'store/shared';

import styles from './Sidebar.module.scss';
import useGetPartnerNotes from 'hooks/partnerNotes/useGetPartnerNotes';
import { PartnerNotesData } from 'store/partnerNotes';
import { getPartnersRecsItemsDesktop } from './helpers/getPartnersRecsItems';
import useGetMobilePartnerNotesItems from 'hooks/partnerNotes/mobile/useGetMobilePartnerNotesItems';
import { getMobilePartnersRecsItems } from './helpers/getMobilePartnersRecsItems';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { TrackEvent } from 'contexts/analytics/types';

export type SidebarItemConfig = {
  to: string;
  icon: IconName;
  label: string;
  onClick?: () => void;
  state?: {
    tinyAccountId?: string | null;
    kitId?: string | null;
    from?: string | null;
  };
  external?: boolean;
  bottom?: boolean;
  hasKitId?: boolean;
  customClassName?: string;
};
const MENU_ITEMS: {
  [key: string]: SidebarItemConfig;
} = {
  home: {
    to: '/',
    icon: 'home',
    label: 'Home',
  },
  results: {
    to: '/results/:kitId',
    icon: 'lightOn',
    label: 'Results',
  },
  actionPlan: {
    to: '/action-plan/:kitId',
    icon: 'today',
    label: 'Action plan',
  },
  kits: {
    to: '/kits',
    icon: 'add',
    label: 'Kits',
  },
  bookmarks: {
    to: '/bookmarks',
    icon: 'bookmark',
    label: 'Bookmarks',
  },
  membership: {
    to: '/membership',
    icon: 'award',
    label: 'Tiny+',
    customClassName: styles.purple,
  },
  resources: {
    to: '/guides',
    icon: 'book',
    label: 'Resources',
  },
  guides: {
    to: '/guides',
    icon: 'book',
    label: 'Resources',
  },
  store: {
    to: urls.STORE,
    icon: 'shoppingCart',
    label: 'Store',
    external: true,
  },
  consults: {
    to: '/consults',
    icon: 'messageHearthSquare',
    label: 'Coaching',
  },
  help: {
    to: '/help',
    icon: 'help',
    label: 'Need Help?',
  },
  referAFriend: {
    to: '/refer-a-friend',
    icon: 'gift',
    label: 'Invite a friend',
  },
  facebook: {
    to: urls.FACEBOOK_GROUP,
    icon: 'userMultiple',
    label: 'Join our Facebook community',
    external: true,
  },
  account: {
    to: '/account',
    icon: 'userSingle',
    label: 'Account',
  },
};

const getHomePageSidebarItems = (
  kitId: string | null,
  tinyAccountId: string | null,
  storeSidebarItem: SidebarItemConfig,
  showMembershipItem: boolean,
  showStoreItem: boolean,
  showReferralItem: boolean,
  partnerNotesData: PartnerNotesData,
  trackEvent: TrackEvent,
  resultsReadyKitId?: string,
): SidebarItemConfig[] => {
  const items = [
    MENU_ITEMS.home,
    MENU_ITEMS.results,
    MENU_ITEMS.actionPlan,
    ...getPartnersRecsItemsDesktop(partnerNotesData, trackEvent),
    MENU_ITEMS.resources,
    { ...MENU_ITEMS.bookmarks },
    { ...MENU_ITEMS.account, bottom: true },
    { ...MENU_ITEMS.help, bottom: true },
  ];

  // Add referral item if not hidden by partner config
  if (showReferralItem) {
    items.push({ ...MENU_ITEMS.referAFriend, bottom: true });
  }

  // Add store item if not hidden by partner config
  if (showStoreItem) {
    items.splice(6, 0, { ...storeSidebarItem, bottom: true });
  }
  // Add membership item if enabled
  if (showMembershipItem) {
    items.splice(5, 0, MENU_ITEMS.membership);
  }

  const navKitId = resultsReadyKitId ?? kitId;
  return items.map(item => ({
    ...item,
    to: item.to.replace(':kitId', navKitId ?? ''),
    state: { tinyAccountId, ...item.state },
  }));
};
const getMobileSidebarItems = (
  storeSidebarItem: SidebarItemConfig,
  showStoreItem: boolean,
  showReferralItem: boolean,
  mobilePartnerNotes: PartnerNotesData[string][],
  trackEvent: TrackEvent,
): SidebarItemConfig[] => {
  const items = [
    MENU_ITEMS.account,
    MENU_ITEMS.guides,
    ...getMobilePartnersRecsItems(mobilePartnerNotes, trackEvent),
    MENU_ITEMS.bookmarks,
  ];

  // Add referral item if not hidden by partner config
  if (showReferralItem) {
    items.push(MENU_ITEMS.referAFriend);
  }

  items.push(MENU_ITEMS.facebook);
  items.push(MENU_ITEMS.help);

  // Add store item if not hidden by partner config
  if (showStoreItem) {
    items.splice(2, 0, storeSidebarItem);
  }

  return items;
};
export const useSidebarItems = ({
  kitId,
  isMobile = false,
  resultsReadyKitId,
}: {
  kitId?: string;
  isMobile?: boolean;
  resultsReadyKitId?: string;
}) => {
  const { isFeatureEnabled: isMembershipPageEnabled } =
    useFeature('membership_page');
  const kit = useSelector(getKitById(kitId));
  const tinyAccountId = kit?.tinyaccount_id;

  const allKits = useSelector(getKits);
  const hasPartnerKits = allKits.some((k: Kit) => !!k.partner_id);

  const partnerNotesData = useGetPartnerNotes();
  const [_, ...mobilePartnerNotes] =
    useGetMobilePartnerNotesItems(partnerNotesData);
  const { trackEvent } = useAnalytics();

  const { isAnyTinyPlus } = useSubscriptions();
  const { checkoutUrl } = useCheckoutKit({
    kitType: kit?.sampling_type,
    nonMemberUrl: urls.STORE,
  });
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    usePortalConfigs();

  const hideUpsells =
    Boolean(portalConfig.hide_upsells) || isPortalConfigLoading;

  // Hide membership if partner config says so
  const showMembershipItem =
    isMembershipPageEnabled &&
    !isAnyTinyPlus &&
    !hasPartnerKits &&
    !hideUpsells;
  // Hide store if partner config says so
  const showStoreItem = !hideUpsells;
  // Hide referral if partner config says so (referral is store-related)
  const showReferralItem = !hideUpsells;

  const storeSidebarItem: SidebarItemConfig = {
    to: checkoutUrl,
    icon: 'shoppingCart',
    label: isAnyTinyPlus ? 'Tiny+ store' : 'Store',
    external: true,
    bottom: true,
  };

  if (isMobile) {
    return getMobileSidebarItems(
      storeSidebarItem,
      showStoreItem,
      showReferralItem,
      mobilePartnerNotes,
      trackEvent,
    );
  }

  return getHomePageSidebarItems(
    kitId ?? null,
    tinyAccountId ?? null,
    storeSidebarItem,
    showMembershipItem,
    showStoreItem,
    showReferralItem,
    partnerNotesData,
    trackEvent,
    resultsReadyKitId,
  );
};
